import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { applyActionCode, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../../firebase-config';
import Logo from '../../components/logo/logo';
import Button from '../../components/button/button';
import ResetPasswordForm from './reset-password-form';

export default function EmailActionHandlerView() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const actionCode = searchParams.get('oobCode');
  const [title, setTitle] = useState('Behandler...');
  const [text, setText] = useState('');
  const [error, setError] = useState(null);
  const [actionAttempted, setActionAttempted] = useState(false);
  const [displayResetPasswordForm, setDisplayResetPasswordForm] =
    useState(false);
  const [accountEmail, setAccountEmail] = useState('');

  const directChangeId = searchParams.get('emailChangeId');
  const continueUrl = searchParams.get('continueUrl');
  let emailChangeId = directChangeId;

  if (!emailChangeId && continueUrl) {
    try {
      emailChangeId = new URL(continueUrl).searchParams.get('emailChangeId');
    } catch {
      emailChangeId = null;
    }
  }

  useEffect(() => {
    if (actionAttempted) return;

    let cancelled = false;

    const completeEmailChange = async () => {
      if (!emailChangeId) {
        throw new Error('Emailændringen mangler et reference-ID.');
      }

      const response = await fetch('/api/user/complete-email-change', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changeId: emailChangeId }),
      });

      if (!response.ok) {
        throw new Error('Emailændringen kunne ikke færdiggøres.');
      }
    };

    const handleAction = async () => {
      try {
        if (mode === 'verifyEmail') {
          await applyActionCode(auth, actionCode);
          if (cancelled) return;
          setTitle('Din e-mail er verificeret');
          setText(
            'Du kan nu færdiggøre din profil ved at trykke på knappen nedenfor.'
          );
          setActionAttempted(true);
          return;
        }

        if (mode === 'verifyAndChangeEmail') {
          await applyActionCode(auth, actionCode);
          await completeEmailChange();
          await auth.currentUser?.reload();
          if (cancelled) return;
          setTitle('Din email er ændret');
          setText('Du kan fremover logge ind med din nye emailadresse.');
          setActionAttempted(true);
          return;
        }

        if (mode === 'resetPassword') {
          const email = await verifyPasswordResetCode(auth, actionCode);
          if (cancelled) return;
          setAccountEmail(email);
          setDisplayResetPasswordForm(true);
          setTitle('Nulstil dit kodeord');
          setText('Indtast dit nye kodeord og tryk på knappen nedenfor.');
          setActionAttempted(true);
          return;
        }

        // Firebase's hosted handler may already have applied the action and
        // redirected to the continue URL.
        if (emailChangeId) {
          await completeEmailChange();
          if (cancelled) return;
          setTitle('Din email er ændret');
          setText('Du kan fremover logge ind med din nye emailadresse.');
          setActionAttempted(true);
        }
      } catch (actionError) {
        if (!cancelled) {
          setError(actionError);
          setTitle('Linket kunne ikke bruges');
          setText('Linket kan være udløbet eller allerede brugt.');
          setActionAttempted(true);
          console.error(actionError);
        }
      }
    };

    handleAction();

    return () => {
      cancelled = true;
    };
  }, [mode, actionCode, actionAttempted, emailChangeId]);

  // Render component based on the mode or a generic message
  return (
    <div className="centered-layout">
      <Logo />
      <div className="centered-layout__content">
        <div className="centered-layout__main">
          <h3>{title}</h3>
          <p>{text}</p>
          {displayResetPasswordForm && (
            <ResetPasswordForm
              actionCode={actionCode}
              accountEmail={accountEmail}
            />
          )}
          {error && !actionAttempted && <p>{error.message}</p>}
        </div>
        <div className="centered-layout__bottom">
          <Button
            buttonStyle="text-link"
            onClick={() => {
              window.location.href = '/';
            }}
          >
            {!error && mode === 'verifyEmail'
              ? 'Færdiggør profil'
              : 'Tilbage til forsiden'}
          </Button>
        </div>
      </div>
    </div>
  );
}
